import { type ReactNode, useEffect, useRef, useState } from "react"
import { Navigate, Outlet, useLocation, useNavigate, useParams } from "react-router-dom"
import ConfirmModal, { ConfirmModalHandle } from "./ConfirmModal"
import Loader from "./Loader"
import NotFound from "../app/pages/NotFound"
import RequireLocation from "./RequireLocation"
import { useRequest } from "../hooks/useRequest"
import { isSearchBot } from "../lib/isSearchBot"
import {
    cinemaHref,
    getCinemaSlugMeta,
    loadCinemaLists,
    resolveSlugLocation,
    storeCinemaSlug,
    stripCinemaSlug,
} from "../lib/cinemaSlugs"
import { appStore } from "../store/appStore"
import { isCityChange, selectCinemaLocation } from "../lib/locationSwitch"

export default function CinemaPlaceLayout() {
    const { place } = useParams()
    const location = useLocation()
    const navigate = useNavigate()
    const { requestData } = useRequest()
    const confirmRef = useRef<ConfirmModalHandle>(null)
    const locationRef = useRef(location)
    locationRef.current = location
    const meta = getCinemaSlugMeta(place)
    const [allowed, setAllowed] = useState(false)
    const [missing, setMissing] = useState(false)

    useEffect(() => {
        if (!meta) return

        let cancelled = false
        const { cinema, locationSelected } = appStore.getState()
        if (locationSelected && cinema.id === meta.cinemaId) {
            setAllowed(true)
            setMissing(false)
            return
        }

        setAllowed(false)
        setMissing(false)

        const revert = () => {
            const loc = locationRef.current
            const rest = stripCinemaSlug(loc.pathname)
            const target = rest === "/" ? "/afisha" : rest
            navigate(`${cinemaHref(storeCinemaSlug(), target)}${loc.search}`, { replace: true })
        }

        const sync = async () => {
            try {
                const lists = await loadCinemaLists(requestData)
                if (cancelled) return
                const found = resolveSlugLocation(meta.slug, lists)
                if (!found) {
                    setMissing(true)
                    return
                }

                const state = appStore.getState()

                if (isSearchBot()) {
                    appStore.setState({ city: found.city, cinema: found.cinema })
                    setAllowed(true)
                    return
                }

                if (state.locationSelected && state.cinema.id === found.cinema.id) {
                    setAllowed(true)
                    return
                }

                if (state.locationSelected && isCityChange(state.city.id, found.city.id)) {
                    confirmRef.current?.open({
                        title: "Смена города",
                        description: `Вы открыли ${found.meta.seoName} (${found.meta.cityName}). После смены города потребуется повторная авторизация. Вы уверены, что хотите сменить город?`,
                        onDismiss: revert,
                        actions: [
                            {
                                label: "Сменить город",
                                variant: "primary",
                                onClick: () => {
                                    selectCinemaLocation({ city: found.city, cinema: found.cinema })
                                    setAllowed(true)
                                },
                            },
                            {
                                label: "Отмена",
                                variant: "secondary",
                                onClick: revert,
                            },
                        ],
                    })
                    return
                }

                appStore.getState().setLocation({ city: found.city, cinema: found.cinema })
                setAllowed(true)
            } catch {
                if (!cancelled) setMissing(true)
            }
        }

        sync()
        return () => {
            cancelled = true
        }
        // requestData каждый рендер новый
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [meta?.slug, navigate])

    if (!meta) return <NotFound />
    if (missing) return <NotFound />

    return (
        <>
            <ConfirmModal ref={confirmRef} />
            {allowed ? <Outlet /> : <Loader frame />}
        </>
    )
}

/** Старые /afisha и /contacts: если зал известен — на /{slug}/..., иначе пикер как раньше. */
export function MaybeRedirectToCinema({ suffix, children }: { suffix: string; children: ReactNode }) {
    const { cinema, locationSelected } = appStore()
    const { search } = useLocation()
    const slug = locationSelected ? slugForCinemaId(cinema.id) : undefined
    if (slug) {
        return <Navigate to={`${cinemaHref(slug, suffix)}${search}`} replace />
    }
    return <RequireLocation>{children}</RequireLocation>
}
